import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CatMark } from "@/components/CatMark";

// Masked line reveal — mimics an editorial curtain lift on the display type.
function RevealLine({ children, delay = 0.4, className = "", style }) {
  return (
    <div className={`block overflow-hidden pb-[0.12em] ${className}`} style={style}>
      <motion.div
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ delay, duration: 1.15, ease: [0.76, 0, 0.24, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

// Architectural editorial hero — 12-column grid with visible hairlines,
// asymmetric type composition, a single travel path that guides the eye
// from the italic claim down to the CTA, and Michi discovered on the path.
export default function Hero() {
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40, duration: 1.6 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.4]);
  const shapeA = useTransform(scrollYProgress, [0, 1], [0, 100]);

  // Editorial motion tokens — restrained, travel-inspired.
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
      style={{ background: "#FAF8F5" }}
    >
      {/* Cream noise texture overlay — physical depth without decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Warm ambient wash — retained but softened + faded */}
      <motion.div
        style={{ y: shapeA }}
        aria-hidden="true"
        className="absolute -top-56 -left-40 w-[55vw] h-[55vw] rounded-full pointer-events-none opacity-70"
      >
        <div
          className="w-full h-full rounded-full"
          style={{ background: "radial-gradient(circle at 30% 30%, #FF6B2C33, transparent 62%)" }}
        />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-[2] w-full max-w-[1800px] mx-auto pt-16 lg:pt-20 pb-4 lg:pb-6 px-6 md:px-12"
      >
        {/* ARCHITECTURAL GRID LINES — hairlines that expose the structure */}
        <div aria-hidden="true" className="absolute inset-x-6 md:inset-x-12 top-0 bottom-0 pointer-events-none">
          <div className="relative w-full h-full grid grid-cols-12 gap-x-4 md:gap-x-6">
            <div className="col-start-4 h-full border-l border-[color:var(--ink-10)]" />
            <div className="col-start-9 h-full border-l border-[color:var(--ink-10)]" />
          </div>
        </div>

        {/* FOLIO ROW — editorial magazine header */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9, ease }}
          className="relative grid grid-cols-4 md:grid-cols-12 gap-x-4 md:gap-x-6 font-sans text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[color:var(--ink-60)] pb-4 md:pb-5 border-b border-[color:var(--ink-10)]"
        >
          <span className="col-span-2 md:col-span-4">Vol. 01 · Barcelona</span>
          <span className="hidden md:block md:col-span-4 text-center">
            La Michi Agencia · Estudio digital
          </span>
          <span className="col-span-2 md:col-span-4 text-right">Índice 001 / Presentación</span>
        </motion.div>

        {/* MAIN CONTENT GRID */}
        <div className="relative grid grid-cols-4 md:grid-cols-12 gap-x-4 md:gap-x-6 mt-5 md:mt-6">
          {/* Overline — the observation, airy + secondary */}
          <div className="col-span-4 md:col-span-8 md:col-start-1">
            <RevealLine
              delay={0.45}
              className="font-display font-normal display-open text-[18px] sm:text-[22px] md:text-[26px] lg:text-[30px] leading-[1.15] text-[color:var(--ink)]/55"
            >
              La primera impresión
            </RevealLine>
            <RevealLine
              delay={0.6}
              className="font-display font-normal display-open text-[18px] sm:text-[22px] md:text-[26px] lg:text-[30px] leading-[1.15] text-[color:var(--ink)]/55"
            >
              ya no ocurre en persona.
            </RevealLine>
          </div>

          {/* Sun anchor — perfect circle at grid intersection top-right */}
          <motion.div
            aria-hidden="true"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.05, duration: 1.1, ease }}
            className="hidden md:block md:col-start-11 md:col-span-2 row-start-1 justify-self-end self-start pt-1"
          >
            <div
              className="w-[36px] h-[36px] lg:w-[44px] lg:h-[44px] rounded-full"
              style={{ background: "#FF6B2C" }}
            />
          </motion.div>

          {/* MAIN CLAIM — massive display anchor, flush-left aligned.
              Italic line gets a tiny negative left margin to compensate
              for the italic side-bearing so it visually aligns with "Ocurre". */}
          <div className="col-span-4 md:col-span-12 mt-2 md:mt-3 relative">
            <RevealLine
              delay={0.9}
              className="font-display display-open text-[16vw] md:text-[11vw] lg:text-[9vw] leading-[0.88] text-[color:var(--ink)]"
            >
              Ocurre
            </RevealLine>
            <RevealLine
              delay={1.05}
              className="font-italic-display display-open text-[16vw] md:text-[11vw] lg:text-[9vw] leading-[0.88] -ml-[0.04em]"
              style={{ color: "#FF6B2C" }}
            >
              <span>en tu web.</span>
            </RevealLine>
          </div>

          {/* TRAVEL PATH — subtle curve from claim area down to CTA/Michi */}
          <svg
            aria-hidden="true"
            className="absolute pointer-events-none z-[1] hidden md:block"
            style={{ left: "0%", top: "58%", width: "100%", height: "38%" }}
            viewBox="0 0 800 400"
            preserveAspectRatio="none"
          >
            <motion.path
              d="M 60 20 C 240 80, 420 60, 560 200 C 640 280, 720 340, 780 380"
              stroke="#111111"
              strokeWidth="1.2"
              strokeDasharray="2 8"
              strokeLinecap="round"
              fill="none"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.45 }}
              transition={{ delay: 1.4, duration: 2.2, ease }}
            />
          </svg>

          {/* BOTTOM ROW — body + primary CTA + micro hint */}
          <div className="col-span-4 md:col-span-12 mt-5 md:mt-6 grid grid-cols-4 md:grid-cols-12 gap-x-4 md:gap-x-6 items-end">
            {/* Body copy — bottom left, contained */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7, duration: 1.1, ease }}
              className="col-span-4 md:col-span-4 md:col-start-1"
            >
              <p className="font-display text-lg lg:text-[1.25rem] leading-[1.2] display-open pb-2 text-[color:var(--ink)]">
                Landing pages premium para{" "}
                <span className="font-italic-display">pequeños negocios.</span>
              </p>
              <p className="font-sans text-[13px] leading-relaxed text-[color:var(--ink)]/70 max-w-sm">
                Páginas web elegantes, rápidas y listas para lanzar. Una web de la que te sentirás orgulloso de compartir.
              </p>
            </motion.div>

            {/* Primary CTA — dominant, single action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.9, duration: 1.0, ease }}
              className="col-span-4 md:col-span-4 md:col-start-6 flex flex-col items-start gap-2 mt-4 md:mt-0"
            >
              <button
                data-testid="hero-cta-presupuesto"
                onClick={scrollToContact}
                className="btn-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--ink)] focus-visible:ring-offset-[#FAF8F5]"
              >
                Empezar mi proyecto
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" fill="none" />
                </svg>
              </button>
              <span className="font-sans text-[10px] tracking-[0.28em] uppercase text-[color:var(--ink-60)]">
                Barcelona · Envío global
              </span>
            </motion.div>

            {/* Destinos annotation + Michi — discovered at path's end */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.4, duration: 1.0, ease }}
              className="hidden md:flex md:col-span-3 md:col-start-10 flex-col items-end gap-1 text-right"
              aria-hidden="true"
            >
              <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-[color:var(--ink-60)]">
                → Destinos
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12">
                <CatMark />
              </div>
              <div className="font-italic-display text-[12px] text-[color:var(--ink-60)]">
                Michi ha llegado.
              </div>
            </motion.div>
          </div>

          {/* MICRO HINT ROW — bottom bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.6, duration: 1 }}
            className="col-span-4 md:col-span-12 mt-4 md:mt-5 flex items-center justify-between border-t border-[color:var(--ink-10)] pt-3 font-sans text-[10px] tracking-[0.28em] uppercase text-[color:var(--ink-60)]"
          >
            <span className="flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[color:var(--ink)] animate-pulse" />
              Desplaza para descubrir
            </span>
            <span className="hidden md:inline">No hacemos plantillas. Hacemos webs bonitas.</span>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
